import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { EnterpriseAuditPageModel } from "../pageModels/EnterpriseAuditPageModel";
import Loading from "../../components/Loading/Loading";
import ImagePreview from "../../components/ImagePreview/ImagePreview";
import ShowHint from "../../components/ShowHint";
import { globalStore } from "../../models/globalStore";
import { DateUtil } from "../../utils/DateUtil";

const pageModel = new EnterpriseAuditPageModel();

const EnterpriseAuditPage: React.FC = observer(() => {
    const [activeTab, setActiveTab] = useState<"pending" | "processed">("pending");
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
    const [currentImageUrl, setCurrentImageUrl] = useState("");
    const [hintVisible, setHintVisible] = useState(false);
    const [hintMessage, setHintMessage] = useState("");

    useEffect(() => {
        globalStore.selectedMenuKey = "enterprise-audit";
        pageModel.loadCandidates();
        pageModel.loadProcessed();
    }, []);

    const handleImagePreview = (imageUrl: string) => {
        setCurrentImageUrl(imageUrl);
        setImagePreviewVisible(true);
    };

    const handleCopy = async (text: string, fieldName: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setHintMessage(`${fieldName}已复制到剪贴板`);
            setHintVisible(true);
        } catch (err) {
            console.error('复制失败:', err);
            setHintMessage('复制失败，请重试');
            setHintVisible(true);
        }
    };

    const renderAuditList = (audits: any[]) => {
        return (
            <div style={{ padding: "20px" }}>
                {audits.map(audit => (
                    <div
                        key={audit.id}
                        style={{
                            padding: "15px",
                            border: "1px solid #eee",
                            marginBottom: "10px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            background: "#fff"
                        }}
                        onClick={() => pageModel.setSelectedAudit(audit)}
                    >
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            width: "100%",
                            gap: "20px"
                        }}>
                            {/* 企业名称 */}
                            <div style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                minWidth: "200px",
                                gap: "8px"
                            }}>
                                {audit.status === 1 && (
                                    <span style={{
                                        background: "#52c41a",
                                        color: "#fff",
                                        fontSize: "12px",
                                        fontWeight: 400,
                                        padding: "2px 6px",
                                        lineHeight: "18px",
                                        flexShrink: 0
                                    }}>
                                        已认证
                                    </span>
                                )}
                                <span style={{ fontSize: "16px", fontWeight: 400, color: "#333" }}>
                                    {audit.companyName}
                                </span>
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                        fontSize: "14px",
                                        color: "#666"
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(audit.companyName, "企业名称");
                                    }}
                                >
                                    <img 
                                        src="/copy_icon.png" 
                                        alt="复制" 
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                </button>
                            </div>

                            {/* 买家信息 */}
                            <div style={{ minWidth: "120px" }}>
                                <span style={{ color: "#666", fontSize: "14px" }}>
                                    买家：{audit.buyerNickname}
                                </span>
                            </div>

                            {/* 电话 */}
                            <div style={{ 
                                display: "flex", 
                                alignItems: "center", 
                                minWidth: "140px",
                                gap: "8px"
                            }}>
                                <span style={{ color: "#666", fontSize: "14px" }}>
                                    电话：{audit.buyerPhone}
                                </span>
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px",
                                        fontSize: "12px",
                                        color: "#666"
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleCopy(audit.buyerPhone, "电话号码");
                                    }}
                                >
                                    <img 
                                        src="/copy_icon.png" 
                                        alt="复制" 
                                        style={{ width: "16px", height: "16px" }}
                                    />
                                </button>
                            </div>

                            {/* 地址 */}
                            <div style={{ 
                                minWidth: "200px", 
                                maxWidth: "200px",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}>
                                <span style={{ color: "#666", fontSize: "14px" }}>
                                    地址：{audit.companyAddress}
                                </span>
                            </div>

                            {/* 提交时间 */}
                            <div style={{ minWidth: "150px" }}>
                                <span style={{ color: "#666", fontSize: "14px" }}>
                                    提交：{audit.submitTime ? DateUtil.formatDatetime(audit.submitTime) : "-"}
                                </span>
                            </div>

                            {/* 拒绝理由 */}
                            {audit.status === 2 && audit.refuseReason && (
                                <div style={{ 
                                    minWidth: "200px",
                                    color: "#ff4d4f",
                                    fontSize: "14px"
                                }}>
                                    拒绝理由：{audit.refuseReason}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const renderDetailModal = () => {
        if (!pageModel.selectedAudit) return null;
        const audit = pageModel.selectedAudit;

        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000
            }}>
                <div style={{
                    background: "white",
                    width: "900px",
                    maxWidth: "90vw",
                    height: "90vh",
                    display: "flex",
                    flexDirection: "column"
                }}>
                    {/* 标题行 */}
                    <div style={{
                        padding: "16px 24px",
                        borderBottom: "1px solid #eee",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center"
                    }}>
                        <h3 style={{ margin: 0, fontWeight: 400, fontSize: "20px" }}>
                            {audit.companyName} - 企业认证审核
                        </h3>
                        <div>
                            {audit.status === 0 && (
                                <>
                                    <button
                                        style={{
                                            marginRight: "10px",
                                            padding: "6px 12px",
                                            background: "#52c41a",
                                            color: "white",
                                            border: "none",
                                            cursor: "pointer",
                                            fontWeight: 400
                                        }}
                                        onClick={() => pageModel.setShowConfirmDialog(true)}
                                    >
                                        通过审核
                                    </button>
                                    <button
                                        style={{
                                            marginRight: "10px",
                                            padding: "6px 12px",
                                            background: "#ff4d4f",
                                            color: "white",
                                            border: "none",
                                            cursor: "pointer",
                                            fontWeight: 400
                                        }}
                                        onClick={() => pageModel.setShowRefuseDialog(true)}
                                    >
                                        拒绝通过
                                    </button>
                                </>
                            )}
                            <button
                                style={{
                                    padding: "6px 12px",
                                    background: "#f5f5f5",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 400
                                }}
                                onClick={() => pageModel.setSelectedAudit(null)}
                            >
                                关闭
                            </button>
                        </div>
                    </div>
                    
                    {/* 内容区域 */}
                    <div style={{ flex: 1, padding: "24px", overflow: "auto" }}>
                        <div style={{ marginBottom: "24px" }}>
                            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 400, color: "#333" }}>
                                企业基本信息
                            </h3>
                            <div style={{ 
                                display: "grid", 
                                gridTemplateColumns: "1fr 1fr", 
                                gap: "16px",
                                marginBottom: "24px"
                            }}>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>企业名称：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.companyName}</span>
                                    <button
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            marginLeft: "8px",
                                            fontSize: "14px",
                                            color: "#666"
                                        }}
                                        onClick={() => handleCopy(audit.companyName, "企业名称")}
                                    >
                                        <img 
                                            src="/copy_icon.png" 
                                            alt="复制" 
                                            style={{ width: "16px", height: "16px" }}
                                        />
                                    </button>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>税号：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.taxNo}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 300 }}>联系电话：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.telNo}</span>
                                    <button
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: "4px",
                                            marginLeft: "8px",
                                            fontSize: "14px",
                                            color: "#666"
                                        }}
                                        onClick={() => handleCopy(audit.telNo, "联系电话")}
                                    >
                                        <img 
                                            src="/copy_icon.png" 
                                            alt="复制" 
                                            style={{ width: "16px", height: "16px" }}
                                        />
                                    </button>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>公司地址：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.companyAddress}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>公司电话：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.companyTelno}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>公司邮箱：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.companyEmail}</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 400, color: "#333" }}>
                                银行信息
                            </h3>
                            <div style={{ 
                                display: "grid", 
                                gridTemplateColumns: "1fr 1fr", 
                                gap: "16px"
                            }}>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>开户行：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.bankName}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>银行卡号：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.bankCardNo}</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 400, color: "#333" }}>
                                申请人信息
                            </h3>
                            <div style={{ 
                                display: "grid", 
                                gridTemplateColumns: "1fr 1fr", 
                                gap: "16px"
                            }}>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>买家昵称：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.buyerNickname}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>买家电话：</span>
                                    <span style={{ fontWeight: 400 }}>{audit.buyerPhone}</span>
                                </div>
                                <div>
                                    <span style={{ color: "#666", fontWeight: 400 }}>提交时间：</span>
                                    <span style={{ fontWeight: 400 }}>
                                        {audit.submitTime ? DateUtil.formatDatetime(audit.submitTime) : "-"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: "24px" }}>
                            <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 400, color: "#333" }}>
                                营业执照
                            </h3>
                            <button
                                style={{
                                    padding: "8px 16px",
                                    background: "#1890ff",
                                    color: "white",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 300
                                }}
                                onClick={() => handleImagePreview(audit.licenseImageUrl)}
                            >
                                查看营业执照
                            </button>
                        </div>

                        {audit.status === 2 && audit.refuseReason && (
                            <div style={{ marginBottom: "24px" }}>
                                <h3 style={{ margin: "0 0 16px 0", fontSize: "18px", fontWeight: 400, color: "#333" }}>
                                    审核记录
                                </h3>
                                <div style={{ 
                                    padding: "16px", 
                                    background: "#fff2f0", 
                                    border: "1px solid #ffccc7",
                                    color: "#cf1322"
                                }}>
                                    <div style={{ fontWeight: 400, marginBottom: "8px" }}>
                                        拒绝理由：{audit.refuseReason}
                                    </div>
                                    {audit.auditTime && (
                                        <div style={{ fontWeight: 400, fontSize: "14px" }}>
                                            审核时间：{DateUtil.formatDatetime(audit.auditTime)}
                                        </div>
                                    )}
                                    {audit.auditorName && (
                                        <div style={{ fontWeight: 400, fontSize: "14px" }}>
                                            审核员：{audit.auditorName}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderConfirmDialog = () => {
        if (!pageModel.showConfirmDialog) return null;
        
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1100
            }}>
                <div style={{
                    background: "white",
                    width: "400px",
                    padding: "24px",
                    fontWeight: 400
                }}>
                    <h3 style={{ margin: "0 0 16px 0", fontWeight: 400 }}>确认审核通过</h3>
                    <p style={{ fontWeight: 400 }}>确认通过该企业的认证审核吗？</p>
                    <div style={{ marginTop: "24px", textAlign: "right" }}>
                                                    <button
                                style={{
                                    marginRight: "10px",
                                    padding: "6px 12px",
                                    background: "#f5f5f5",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 400
                                }}
                                onClick={() => pageModel.setShowConfirmDialog(false)}
                            >
                                取消
                            </button>
                        <button
                            style={{
                                padding: "6px 12px",
                                background: "#52c41a",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: 400
                            }}
                            onClick={() => pageModel.approveAudit()}
                        >
                            确认
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderRefuseDialog = () => {
        if (!pageModel.showRefuseDialog) return null;
        
        return (
            <div style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1100
            }}>
                <div style={{
                    background: "white",
                    width: "400px",
                    padding: "24px",
                    fontWeight: 400
                }}>
                    <h3 style={{ margin: "0 0 16px 0", fontWeight: 400 }}>拒绝通过</h3>
                    <div style={{ marginBottom: "16px" }}>
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: 400 }}>
                            拒绝原因：
                        </label>
                        <textarea
                            style={{
                                width: "100%",
                                height: "100px",
                                padding: "8px",
                                border: "1px solid #d9d9d9",
                                resize: "vertical",
                                fontFamily: "inherit",
                                fontSize: "14px"
                            }}
                            value={pageModel.refuseReason}
                            onChange={(e) => pageModel.setRefuseReason(e.target.value)}
                            placeholder="请输入拒绝原因..."
                        />
                    </div>
                    <div style={{ textAlign: "right" }}>
                                                    <button
                                style={{
                                    marginRight: "10px",
                                    padding: "6px 12px",
                                    background: "#f5f5f5",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 400
                                }}
                                onClick={() => pageModel.setShowRefuseDialog(false)}
                            >
                                取消
                            </button>
                        <button
                            style={{
                                padding: "6px 12px",
                                background: "#ff4d4f",
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: 400
                            }}
                            onClick={() => pageModel.refuseAudit()}
                            disabled={!pageModel.refuseReason.trim()}
                        >
                            确认
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ height: "100vh", display: "flex", flexDirection: "column", fontWeight: 400 }}>
            {/* TAB切换 */}
            <div style={{ padding: "30px 20px", paddingBottom: "0px" }}>
                <button
                    style={{
                        padding: "8px 16px",
                        marginRight: "10px",
                        border: "none",
                        background: activeTab === "pending" ? "#1890ff" : "#f5f5f5",
                        color: activeTab === "pending" ? "white" : "#666",
                        cursor: "pointer",
                        fontWeight: 400
                    }}
                    onClick={() => setActiveTab("pending")}
                >
                    待审核
                </button>
                <button
                    style={{
                        padding: "8px 16px",
                        border: "none",
                        background: activeTab === "processed" ? "#1890ff" : "#f5f5f5",
                        color: activeTab === "processed" ? "white" : "#666",
                        cursor: "pointer",
                        fontWeight: 400
                    }}
                    onClick={() => setActiveTab("processed")}
                >
                    已审核
                </button>
            </div>

            {/* 内容区域 */}
            <div style={{ flex: 1, overflow: "auto" }}>
                {renderAuditList(activeTab === "pending" ? pageModel.candidates : pageModel.processed)}
            </div>

            {/* 详情弹框 */}
            {renderDetailModal()}

            {/* 确认弹框 */}
            {renderConfirmDialog()}

            {/* 拒绝弹框 */}
            {renderRefuseDialog()}

            {/* 图片预览 */}
            <ImagePreview
                visible={imagePreviewVisible}
                src={currentImageUrl}
                title="营业执照预览"
                onClose={() => setImagePreviewVisible(false)}
            />

            {/* Loading */}
            {pageModel.loading && <Loading />}

            {/* 提示信息 */}
            <ShowHint
                message={hintMessage}
                visible={hintVisible}
                onClose={() => setHintVisible(false)}
                duration={2000}
                position="top"
            />
        </div>
    );
});

export default EnterpriseAuditPage; 